<template>
  <div>
    <label
      class="checkbox"
      :class="[
        { 'error': errors.length },
        { 'checkbox--indeterminate': indeterminate },
        { 'checkbox--disabled': disabled },
      ]"
      :for="name"
    >
      <input
        :id="name"
        ref="checkbox"
        :checked.prop="indeterminate ? undefined : checked"
        :indeterminate.attr="indeterminate"
        :name="name"
        type="checkbox"
        :class="{ 'checkbox__box--error error': errors.length }"
        @input="handleChange"
        @blur="handleBlur"
      />
      <span class="checkbox__box">
        <div v-if="indeterminate" class="checkbox__box-icon checkbox__box-icon--indeterminate" />
        <UIIcon v-else class="checkbox__box-icon" name="check" filled></UIIcon>
      </span>
      <span class="checkbox__label"><slot /></span>
    </label>
    <div v-show="errorMessage" class="checkbox-error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { toRef } from 'vue'
import { useField } from 'vee-validate'

const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    value?: boolean
    id?: string
    name: string
    syncVModel?: boolean
    controlled?: boolean
    indeterminate?: boolean
    disabled?: boolean
  }>(),
  {
    modelValue: false,
    value: false,
    id: undefined,
    syncVModel: false,
    controlled: true,
    indeterminate: false,
    disabled: false,
  },
)

const name = toRef(props, 'name')

const { checked, handleBlur, handleChange, errorMessage, errors } = useField(name, undefined, {
  type: 'checkbox',
  syncVModel: props.syncVModel,
  controlled: props.controlled,
  checkedValue: true,
  uncheckedValue: false,
})

const checkbox = ref<HTMLInputElement | null>(null)

watch(
  () => props.indeterminate,
  value => {
    if (checkbox.value) {
      checkbox.value.indeterminate = value
    }
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
@import '@/styles/mixins.scss';

.checkbox {
  display: flex;
  align-items: center;
  user-select: none;

  &__label {
    display: flex;
    align-items: center;
    cursor: pointer;
    @include body2();

    &:not(:empty) {
      margin-left: 10px;
    }
  }

  &__box {
    display: block;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border: $base-border-width solid $black-100;
    border-radius: 4px;
    transition:
      $base-transition background,
      $base-transition border-color,
      $base-transition outline;
    position: relative;
    color: white;
    cursor: pointer;

    &:focus {
      border: $base-border-width solid $black-100;
    }

    &-icon {
      transition: $base-transition opacity;
      width: 17px;
      height: 17px;
      opacity: 0;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      &--indeterminate {
        width: 12px;
        height: 1px;
        background: white;
        border-radius: 2px;
      }
    }
  }

  &--indeterminate {
    .checkbox__box {
      background: $black-100;
      border: $base-border-width solid $black-100;

      .checkbox__box-icon {
        opacity: 1;
      }
    }
  }

  &--disabled {
    pointer-events: none;

    .checkbox__box {
      border: $base-border-width solid $black-30;
    }

    input[type='checkbox'] {
      &:checked ~ .checkbox__box,
      &:indeterminate ~ .checkbox__box {
        background: $black-30 !important;
        border: $base-border-width solid $black-30 !important;
      }
    }
  }

  &:hover {
    input {
      &:checked ~ .checkbox__box {
        background: $gold-primary-100;

        &:hover {
          background: $gold-primary-100;
        }
      }
    }
    .checkbox__box {
      border: $base-border-width solid $gold-primary-100;
    }
  }

  input {
    position: absolute;
    width: 20px;
    height: 20px;
    opacity: 0;

    &:focus ~ .checkbox__box {
      border: $base-border-width solid $gold-primary-100;
    }

    &:checked ~ .checkbox__box,
    &:indeterminate ~ .checkbox__box {
      background: $black-100;

      &:hover {
        background: $gold-primary-100;
        border: $base-border-width solid $gold-primary-100;
      }

      .checkbox__box-icon {
        opacity: 1;
      }
    }
  }
}
</style>
